package io.horion.auth.presentation.ui.pages.password

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.components.icons.fa.FaCircleExclamation
import com.varabyte.kobweb.silk.components.icons.fa.FaLock
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import io.horion.auth.di.koinGet
import io.horion.auth.domain.models.AppLanguage
import io.horion.auth.presentation.ui.components.auth.AuthButton
import io.horion.auth.presentation.ui.components.auth.AuthTextField
import io.horion.auth.presentation.ui.components.layouts.PageLayoutData
import io.horion.auth.presentation.ui.pages.navigation.Screen
import io.horion.auth.presentation.ui.styles.AuthContentStyle
import io.horion.auth.presentation.ui.styles.AuthTitleStyle
import io.horion.auth.presentation.ui.styles.PageWrapperStyle
import io.horion.auth.presentation.ui.theme.AuthSpacing
import io.horion.auth.presentation.ui.theme.AuthTheme
import io.horion.auth.presentation.ui.theme.AuthTypography
import io.horion.auth.presentation.viewmodel.AuthViewModel
import io.horion.auth.utils.Validators
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Input

@InitRoute
fun initForgetPasswordPage(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData(AppLanguage.strings.pageTitleForget))
}

@OptIn(DelicateApi::class)
@Page("/forget-password")
@Layout("io.horion.auth.presentation.ui.components.layouts.PageLayout")
@Composable
fun forgetPasswordPage() {
    val ctx = rememberPageContext()
    val colorMode = ColorMode.current
    val colors = AuthTheme.colors(colorMode)
    val authViewModel = koinGet<AuthViewModel>()
    val authUiState = authViewModel.uiState
    val strings = AppLanguage.strings

    var passwordConfirm by remember { mutableStateOf("") }

    LaunchedEffect(authUiState.verifySucceed) {
        if (!authUiState.verifySucceed) {
            //authViewModel.verify
            ctx.router.navigateTo(Screen.Login.route)
        }
    }

    LaunchedEffect(authUiState.resetPasswordSucceed) {
        if (authUiState.resetPasswordSucceed) {
            // Attendre 2 secondes pour montrer le message de succès
            ctx.router.navigateTo(Screen.Login.route)
        }
    }

    Box(modifier = PageWrapperStyle.toModifier(), contentAlignment = Alignment.TopCenter) {
        Column(modifier = AuthContentStyle.toModifier(), horizontalAlignment = Alignment.CenterHorizontally) {
            Box(modifier = Modifier.height(AuthSpacing.xl))

            SpanText(
                strings.resetTitle,
                modifier = AuthTitleStyle.toModifier().fontWeight(FontWeight.Bold).color(colors.onBackground)
            )
            Box(modifier = Modifier.height(4.px))
            SpanText(
                strings.forgetSubtitle,
                modifier = Modifier.fontSize(AuthTypography.small).color(colors.mutedText)
            )
            Box(modifier = Modifier.height(AuthSpacing.xl))

            authUiState.authErrorMessage?.let { error ->
                Row(
                    modifier = Modifier
                        .fillMaxWidth().padding(12.px).borderRadius(8.px)
                        .border(1.px, LineStyle.Solid, colors.error)
                        .styleModifier { property("background-color", "rgba(239,68,68,0.08)") }
                        .gap(8.px),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    FaCircleExclamation(modifier = Modifier.color(colors.error).fontSize(14.px))
                    SpanText(error, modifier = Modifier.fontSize(AuthTypography.small).color(colors.error))
                }
                Box(modifier = Modifier.height(AuthSpacing.md))
            }

            // Champ OTP + bouton "Envoyer le code"
            OtpInlineField(
                value = authUiState.code,
                onValueChange = { authViewModel.onChangeCode(it) },
                error = authUiState.codeError,
                cooldownSeconds = authUiState.otpCooldownSeconds,
                otpEnabled = true,
                onSendOtp = { authViewModel.forgotPassword() },
                colorMode = colorMode
            )
            Box(modifier = Modifier.height(AuthSpacing.sm))

            AuthTextField(
                modifier = Modifier.fillMaxWidth(),
                value = authUiState.password,
                onValueChange = { authViewModel.onPasswordChange(it) },
                placeholder = strings.newPasswordPlaceholder,
                type = InputType.Password,
                leadingIcon = { FaLock(modifier = Modifier.color(colors.secondary)) },
                error = authUiState.passwordError,
                success = Validators.isValidPassword(authUiState.password)
            )
            Box(modifier = Modifier.height(AuthSpacing.sm))

            AuthTextField(
                modifier = Modifier.fillMaxWidth(),
                value = passwordConfirm,
                onValueChange = { passwordConfirm = it },
                placeholder = strings.newPasswordPlaceholder,
                type = InputType.Password,
                leadingIcon = { FaLock(modifier = Modifier.color(colors.secondary)) },
                error = authUiState.passwordError,
                success = authUiState.password.isNotEmpty() && authUiState.password == passwordConfirm
            )

            Box(modifier = Modifier.height(AuthSpacing.sm))

            AuthButton(
                text = "SUIVANT",
                onClick = { authViewModel.resetPassword() },
                loading = authUiState.isLoading,
                disabled = authUiState.code.length != 6
                        && authUiState.password.isNotBlank()
                        && authUiState.password == passwordConfirm
                        && Validators.isValidPassword(authUiState.password)
            )
        }
    }
}

@Composable
private fun OtpInlineField(
    value: String,
    onValueChange: (String) -> Unit,
    error: String?,
    cooldownSeconds: Int,
    otpEnabled: Boolean,
    onSendOtp: () -> Unit,
    colorMode: ColorMode
) {
    val colors = AuthTheme.colors(colorMode)
    val isInCooldown = cooldownSeconds > 0
    val strings = AppLanguage.strings
    Column(modifier = Modifier.fillMaxWidth()) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .borderRadius(12.px)
                .border(
                    2.px,
                    LineStyle.Solid,
                    if (error != null) colors.error else colors.border
                )
                .backgroundColor(colors.inputBg),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Input(type = InputType.Text, attrs = {
                value(value)
                onInput {
                    if (otpEnabled) {
                        onValueChange(it.value.filter(Char::isDigit).take(6))
                    }
                }
                attr("placeholder", if (otpEnabled) strings.otpPlaceholder else strings.otpWaitPlaceholder)
                attr("maxlength", "6")
                attr("inputmode", "numeric")
                if (!otpEnabled) attr("disabled", "true")

                style {
                    property("background", "transparent")
                    property("border", "none")
                    property("outline", "none")
                    property("color", colors.onBackground.toString())
                    property("flex", "1")
                    property("font-size", "14px")
                    property("padding", "0 14px")
                    property("height", "48px")
                    property("width", "100%")
                }
            })

            Box(
                modifier = Modifier
                    .width(1.px)
                    .height(32.px)
                    .backgroundColor(colors.borderLight)
            )

            Box(
                modifier = Modifier
                    .padding(leftRight = 14.px, topBottom = 12.px)
                    .cursor(if (!isInCooldown) Cursor.Pointer else Cursor.NotAllowed)
                    .styleModifier { property("flex-shrink", "0") }
                    .onClick {
                        if (!isInCooldown) onSendOtp()
                    },
                contentAlignment = Alignment.Center
            ) {
                SpanText(
                    when {
                        isInCooldown -> "${strings.resendCode} (${cooldownSeconds}s)"
                        otpEnabled -> strings.resendCode
                        else -> strings.sendCode
                    },
                    modifier = Modifier
                        .fontSize(AuthTypography.tiny)
                        .fontWeight(FontWeight.SemiBold)
                        .color(if (!isInCooldown) colors.primary else colors.mutedText)
                        .styleModifier { property("white-space", "nowrap") }
                )
            }
        }

        error?.let {
            SpanText(
                it,
                modifier = Modifier
                    .color(colors.error)
                    .fontSize(AuthTypography.tiny)
                    .padding(top = 6.px, left = 12.px)
            )
        }
    }
}